import {
  Button,
  Checkbox,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  Progress,
  SegmentedControl,
  Select,
  TextInput,
} from '@mantine/core';
import {
  checkTemplate,
  firstName,
  isCancelled,
  joinNames,
  MAIL_DEFS,
  mailboxGroups,
  noticeProblems,
  PLACEHOLDERS,
  type Lang,
  type PlaceholderKey,
} from '@reditelji/shared';
import type { Editor } from '@tiptap/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEvent } from '../api/hooks';
import {
  useMailBatch,
  useMailNotice,
  useMailNotices,
  useNoticeMutations,
  useNoticePreview,
  type MailPreview,
} from '../api/mail';
import { useAllShifts, usePosts, useSectors } from '../api/posts';
import { useTags } from '../api/tags';
import { useVolunteers, type VolunteerRow } from '../api/volunteers';
import { plural } from '../lib/ui';
import { schedStateLabel } from '../volunteers/derive';
import { TagFilterChips } from '../volunteers/TagBits';
import { countUsage } from '../volunteers/tagLogic';
import { MailPreviewPane } from './MailPreview';
import type { HandoffNote, useNoticeDraft } from './noticeDraft';
import { draftHasText } from './noticeDraft';
import {
  EMPTY_FILTERS,
  langsOf,
  liveNoticeFilters,
  matchesNotice,
  NOTICE_STATES,
  noticeCandidates,
  prunePicked,
  unitMates,
  unpickedHousemates,
  type NoticeCandidate,
  type UnpickedHousemate,
} from './noticeFilters';
import { BatchProgress, BatchReport, BatchReportLists } from './Recipients';
import { householdsIn, mailCount, shortDate } from './sendState';
import { BodyEditor, PlaceholderPalette } from './TemplateEditor';
import { Warnings } from './Warnings';
import { bySuffix, useCoordinatorLabel } from '../lib/people';

/** Above this many recipients the send asks for their number to be typed. */
const TYPE_TO_CONFIRM_ABOVE = 50;

/** The server refuses more than this in one request (mailNoticeSendSchema). */
const BULK_LIMIT = 300;

const LANG_WORD: Record<Lang, string> = { sl: 'slovensko', en: 'angleško' };

const people = (n: number) => plural(n, 'prejemnik', 'prejemnika', 'prejemniki', 'prejemnikov');

type NoticeDraftHandle = ReturnType<typeof useNoticeDraft>;

/**
 * Obvestila — a mail written for one send, to people picked for it (SPEC §1.1H).
 *
 * The rail holds the one being written and every one sent before it. What is
 * being written is kept in this browser until it goes; what went is a record
 * that is only ever read.
 */
export function Notices({
  notice,
  onClear,
  selected,
  onSelect,
  handoffNote,
  onDismissHandoff,
}: {
  notice: NoticeDraftHandle;
  /** Empty the draft — after a send, or when asked — and whatever was said about how it was filled. */
  onClear: () => void;
  /** `'new'` for the one being written, otherwise a sent obvestilo's batch id. */
  selected: string;
  onSelect: (id: string) => void;
  handoffNote: HandoffNote | null;
  onDismissHandoff: () => void;
}) {
  const labelOf = useCoordinatorLabel();
  const notices = useMailNotices();
  const writing = draftHasText(notice.draft) || notice.draft.picked.length > 0;

  return (
    <div className="ml-layout">
      <nav className="ml-rail">
        <div className="ml-eyebrow">OBVESTILA</div>
        <button
          type="button"
          className={`ml-railitem${selected === 'new' ? ' ml-railitem--active' : ''}`}
          onClick={() => onSelect('new')}
        >
          <span className="ml-railitem-name">Novo obvestilo</span>
          <span className="ml-railitem-state">
            {writing ? 'osnutek v tem brskalniku' : 'enkratno sporočilo izbranim'}
          </span>
        </button>

        {(notices.data ?? []).length > 0 ? (
          <div className="ml-eyebrow" style={{ marginTop: 16 }}>
            POSLANA
          </div>
        ) : null}
        {(notices.data ?? []).map((n) => (
          <button
            key={n.id}
            type="button"
            className={`ml-railitem${selected === n.id ? ' ml-railitem--active' : ''}`}
            onClick={() => onSelect(n.id)}
          >
            <span className="ml-railitem-name">{n.subject || '— brez zadeve —'}</span>
            <span className="ml-railitem-state">
              {n.status === 'done'
                ? `${n.counts.sent} ${people(n.counts.sent)} · ${shortDate(n.createdAt)}${
                    bySuffix(labelOf(n.createdById, n.createdByName, ''))
                  }`
                : `v teku — ${n.counts.total - n.counts.waiting} od ${n.counts.total}`}
            </span>
          </button>
        ))}
      </nav>

      {selected === 'new' ? (
        <NoticeComposer
          notice={notice}
          onClear={onClear}
          handoffNote={handoffNote}
          onDismissHandoff={onDismissHandoff}
          onOpenRecord={onSelect}
        />
      ) : (
        <NoticeRecord key={selected} id={selected} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Writing one
// ---------------------------------------------------------------------------

function NoticeComposer({
  notice,
  onClear,
  handoffNote,
  onDismissHandoff,
  onOpenRecord,
}: {
  notice: NoticeDraftHandle;
  onClear: () => void;
  handoffNote: HandoffNote | null;
  onDismissHandoff: () => void;
  onOpenRecord: (id: string) => void;
}) {
  const { draft, seed, setBody, replaceBody, setFilters, setPicked, setIncludeCancelled } = notice;
  const event = useEvent();
  const volunteers = useVolunteers();
  const shifts = useAllShifts();
  const posts = usePosts();
  const sectors = useSectors();
  const tags = useTags('volunteer');
  const { translate, testSend } = useNoticeMutations();
  const previewMutation = useNoticePreview();

  const [lang, setLang] = useState<Lang>('sl');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [household, setHousehold] = useState(false);
  const [preview, setPreview] = useState<MailPreview | null>(null);
  const [confirming, setConfirming] = useState(false);

  /*
   * Everything the list is drawn from, or nothing. The selection follows the
   * screen, and a screen drawn from half the data would hide people the
   * restored draft had ticked — and take them out of it for good.
   */
  const ready =
    volunteers.isSuccess &&
    shifts.isSuccess &&
    posts.isSuccess &&
    sectors.isSuccess &&
    tags.isSuccess &&
    event.isSuccess;

  const days = useMemo(() => event.data?.days ?? [], [event.data]);
  const candidates = useMemo(
    () =>
      ready
        ? noticeCandidates({
            volunteers: volunteers.data,
            shifts: shifts.data,
            posts: posts.data,
            days,
            includeCancelled: draft.includeCancelled,
          })
        : [],
    [ready, volunteers.data, shifts.data, posts.data, days, draft.includeCancelled],
  );

  const filters = useMemo(
    () =>
      liveNoticeFilters(draft.filters, {
        tagIds: new Map((tags.data ?? []).map((t) => [t.id, t])),
        postIds: new Map((posts.data ?? []).map((p) => [p.id, p])),
        sectorIds: new Map((sectors.data ?? []).map((s) => [s.id, s])),
        dayIds: new Map(days.map((d) => [d.id, d])),
      }),
    [draft.filters, tags.data, posts.data, sectors.data, days],
  );
  const visible = useMemo(
    () => candidates.filter((c) => matchesNotice(c, filters)),
    [candidates, filters],
  );

  // The Prejemniki rule: nobody stays ticked who is no longer on screen.
  useEffect(() => {
    if (!ready) return;
    const next = prunePicked({
      picked: draft.picked,
      addedHousemates: draft.addedHousemates,
      visible,
      candidates,
    });
    if (next !== draft.picked) setPicked(() => [...next]);
  }, [ready, draft.picked, draft.addedHousemates, visible, candidates, setPicked]);

  const pickedSet = useMemo(() => new Set(draft.picked), [draft.picked]);
  const picked = useMemo(
    () => candidates.filter((c) => pickedSet.has(c.v.id)),
    [candidates, pickedSet],
  );
  const langs = useMemo(() => langsOf(draft.picked, candidates), [draft.picked, candidates]);
  const bodies = useMemo(() => ({ sl: draft.sl, en: draft.en }), [draft.sl, draft.en]);
  const problems = noticeProblems(bodies, langs);

  const current = draft[lang];
  const check = checkTemplate('obvestilo', current.subject, current.body);
  const aiEnabled = event.data?.ai.enabled ?? false;

  // A person no longer ticked is no longer somebody to preview.
  const previewPerson = previewFor ? (picked.find((c) => c.v.id === previewFor) ?? null) : null;
  const renderLang: Lang = previewPerson?.v.lang ?? lang;
  const withIds = useMemo(
    () => (previewPerson ? unitMates(previewPerson.v.id, draft.picked, candidates) : []),
    [previewPerson, draft.picked, candidates],
  );
  const renderWords = draft[renderLang];
  const renderInput = useMemo(
    () => ({
      lang: renderLang,
      subject: renderWords.subject,
      body: renderWords.body,
      volunteerId: previewPerson?.v.id ?? null,
      with: withIds,
      household,
      includeCancelled: draft.includeCancelled,
    }),
    [renderLang, renderWords, previewPerson, withIds, household, draft.includeCancelled],
  );

  // The preview follows the draft, one render behind the typing, and a late
  // answer never replaces a newer one.
  const { mutate: renderPreview } = previewMutation;
  const latest = useRef(0);
  useEffect(() => {
    const t = setTimeout(() => {
      const seq = (latest.current += 1);
      renderPreview(renderInput, {
        onSuccess: (out) => {
          if (seq === latest.current) setPreview(out);
        },
      });
    }, 400);
    return () => clearTimeout(t);
  }, [renderPreview, renderInput]);

  const insert = (key: PlaceholderKey) => {
    if (!editor) return;
    if (PLACEHOLDERS[key].block) {
      editor.chain().focus().insertContent({ type: 'placeholderBlock', attrs: { key } }).run();
    } else {
      editor.chain().focus().insertPlaceholder(key).run();
    }
  };

  const selectable = visible.filter((c) => c.unavailable === null).slice(0, BULK_LIMIT);
  const allSelectable = selectable.length > 0 && selectable.every((c) => pickedSet.has(c.v.id));
  // "Select all" can only ever mean the first BULK_LIMIT, so past the cap it
  // stays indeterminate rather than claiming to have selected the whole list.
  const allPicked =
    allSelectable && visible.filter((c) => c.unavailable === null).length <= BULK_LIMIT;
  const overCap = draft.picked.length > BULK_LIMIT;

  // Who shares an address, found once for the list rather than once per row.
  const matesOf = useMemo(() => {
    const out = new Map<string, string>();
    for (const group of mailboxGroups(candidates.map((c) => c.v)).values()) {
      for (const v of group) {
        const others = group.filter((o) => o.id !== v.id).map((o) => firstName(o.name));
        out.set(v.id, others.join(', '));
      }
    }
    return out;
  }, [candidates]);
  const togglePick = useCallback(
    (id: string) =>
      setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])),
    [setPicked],
  );

  const previewHint = previewPerson
    ? preview && preview.people.length > 1
      ? `Skupni predal — ${joinNames(preview.people, 'sl')}: eno sporočilo, ${LANG_WORD[renderLang]}. Nič se ne pošlje.`
      : `Prava imena te osebe, v njenem jeziku (${LANG_WORD[renderLang]}). Nič se ne pošlje.`
    : 'Vzorčni podatki. Izberi prejemnika za pravo vsebino.';

  return (
    <>
      <section className="ml-editor ml-nt-composer">
        <div className="ml-editor-head">
          <div>
            <div className="ml-editor-name">Novo obvestilo</div>
            <div className="ml-hint">{MAIL_DEFS.obvestilo.lede}</div>
          </div>
          <SegmentedControl
            size="xs"
            value={lang}
            onChange={(v) => setLang(v)}
            data={[
              { value: 'sl', label: 'Slovensko' },
              { value: 'en', label: 'English' },
            ]}
          />
        </div>

        {lang === 'en' ? (
          <div className="ml-translate">
            <span className="ml-hint">
              Angleščino dobijo prostovoljci, ki so izbrali angleški jezik
              {langs.includes('en') ? '' : ' — med izbranimi jih ni, zato je lahko prazna'}.
            </span>
            {aiEnabled ? (
              <Button
                size="compact-xs"
                variant="default"
                loading={translate.isPending}
                disabled={draft.sl.subject.trim() === '' && draft.sl.body.trim() === ''}
                onClick={() =>
                  translate.mutate(draft.sl, { onSuccess: (out) => replaceBody('en', out) })
                }
              >
                Prevedi iz slovenščine
              </Button>
            ) : null}
          </div>
        ) : null}

        <TextInput
          size="sm"
          label="Zadeva"
          placeholder="Zadeva sporočila"
          value={current.subject}
          onChange={(e) => setBody(lang, { subject: e.currentTarget.value })}
        />
        <div className="ml-editor-body">
          <BodyEditor
            value={current.body}
            onChange={(html) => setBody(lang, { body: html })}
            templateKey="obvestilo"
            docKey={`obvestilo:${lang}:${seed}`}
            onEditor={setEditor}
          />
        </div>
        {/* The preview's leftovers belong to the language it rendered. */}
        <Warnings
          check={check}
          unresolved={renderLang === lang ? (preview?.unresolved ?? []) : []}
          blocking
        />

        <div className="ml-eyebrow" style={{ marginTop: 18 }}>
          OZNAKE
        </div>
        <div className="ml-hint" style={{ marginBottom: 8 }}>
          Klik vstavi oznako na mesto kazalca. Ob pošiljanju jo aplikacija zamenja s podatkom
          prejemnika — pri skupnem predalu z imeni vseh.
        </div>
        <PlaceholderPalette templateKey="obvestilo" onInsert={insert} />

        <div className="ml-eyebrow" style={{ marginTop: 22 }}>
          PREJEMNIKI
        </div>
        {handoffNote && (handoffNote.cancelled > 0 || handoffNote.noEmail > 0) ? (
          <div className="ml-warn ml-nt-note">
            <span>
              {handoffNote.cancelled > 0
                ? `${handoffNote.cancelled} izbranih iz Prejemnikov ne pride — vključiš jih s kljukico spodaj. `
                : ''}
              {handoffNote.noEmail > 0
                ? `${handoffNote.noEmail} nima e-naslova in ne more prejeti obvestila.`
                : ''}
            </span>
            <Button size="compact-xs" variant="subtle" onClick={onDismissHandoff}>
              Zapri
            </Button>
          </div>
        ) : null}

        {!ready ? (
          <div className="ml-rc-empty">
            <Loader size="sm" />
          </div>
        ) : (
          <>
            <NoticeFilterRow
              filters={draft.filters}
              onFilters={setFilters}
              includeCancelled={draft.includeCancelled}
              onIncludeCancelled={setIncludeCancelled}
              candidates={candidates}
              days={days.map((d) => ({ value: d.id, label: d.label }))}
              sectors={(sectors.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
              posts={postOptions(posts.data ?? [], sectors.data ?? [])}
              tags={tags.data ?? []}
            />

            <div className="ml-nt-list">
              <table className="ml-rc-table">
                <thead>
                  <tr>
                    <th className="ml-rc-check">
                      <Checkbox
                        size="xs"
                        aria-label="Izberi vse prikazane"
                        checked={allPicked}
                        indeterminate={!allPicked && selectable.some((c) => pickedSet.has(c.v.id))}
                        onChange={() =>
                          setPicked((p) =>
                            allSelectable
                              ? p.filter((id) => !selectable.some((c) => c.v.id === id))
                              : [...new Set([...p, ...selectable.map((c) => c.v.id)])],
                          )
                        }
                      />
                    </th>
                    <th className="ml-rc-name">Prostovoljec</th>
                    <th>Jezik</th>
                    <th>Razpored</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((c) => (
                    <RecipientRow
                      key={c.v.id}
                      c={c}
                      picked={pickedSet.has(c.v.id)}
                      mates={matesOf.get(c.v.id) ?? ''}
                      onToggle={togglePick}
                    />
                  ))}
                </tbody>
              </table>
              {visible.length === 0 ? <div className="ml-rc-empty">Ni zadetkov.</div> : null}
            </div>
            <div className="ml-nt-count">
              <b>{draft.picked.length} izbranih</b> · {visible.length} prikazanih
              {overCap ? ` · naenkrat gre lahko največ ${BULK_LIMIT} sporočil — zoži izbor` : ''}
            </div>
          </>
        )}

        <div className="ml-nt-bar">
          {problems.length > 0 && draft.picked.length > 0 ? (
            <ul className="ml-nt-problems">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          ) : null}
          <div className="ml-actions" style={{ marginTop: 0 }}>
            <Menu position="top-start" withArrow>
              <Menu.Target>
                <Button size="xs" variant="subtle" aria-label="Možnosti osnutka">
                  ⋮
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item color="red" onClick={onClear}>
                  Počisti osnutek
                </Menu.Item>
                <Menu.Item onClick={() => setFilters(EMPTY_FILTERS)}>Počisti filtre</Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <div style={{ flex: 1 }} />
            <Button
              size="xs"
              variant="default"
              loading={testSend.isPending}
              disabled={draft[renderLang].subject.trim() === ''}
              onClick={() => testSend.mutate(renderInput)}
            >
              Pošlji poskusno sebi
            </Button>
            <Button
              size="xs"
              disabled={draft.picked.length === 0 || overCap || problems.length > 0}
              onClick={() => setConfirming(true)}
            >
              Pošlji {draft.picked.length > 0 ? draft.picked.length : ''}…
            </Button>
          </div>
        </div>
      </section>

      <MailPreviewPane
        preview={preview}
        pending={previewMutation.isPending}
        volunteerId={previewPerson?.v.id ?? null}
        onVolunteer={setPreviewFor}
        volunteers={picked.map((c) => ({ value: c.v.id, label: c.v.name }))}
        household={household}
        onHousehold={setHousehold}
        hint={previewHint}
      />

      {confirming ? (
        <NoticeConfirm
          notice={notice}
          onSent={onClear}
          picked={picked.map((c) => c.v)}
          problems={problems}
          overCap={overCap}
          housematesToAdd={unpickedHousemates(draft.picked, candidates)}
          onClose={(batchId) => {
            setConfirming(false);
            if (batchId) onOpenRecord(batchId);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * One person in the list. Memoised: typing in the editor re-renders the
 * composer, and a few hundred rows that have not changed should not follow.
 */
const RecipientRow = memo(function RecipientRow({
  c,
  picked,
  mates,
  onToggle,
}: {
  c: NoticeCandidate;
  picked: boolean;
  /** First names of the others on this address, or ''. */
  mates: string;
  onToggle: (id: string) => void;
}) {
  return (
    <tr className={picked ? 'ml-rc-row--picked' : undefined}>
      <td className="ml-rc-check">
        <Checkbox
          size="xs"
          aria-label={c.v.name}
          checked={picked}
          disabled={c.unavailable !== null}
          onChange={() => onToggle(c.v.id)}
        />
      </td>
      <td className="ml-rc-name">
        <span className="ml-rc-person">{c.v.name}</span>
        {c.unavailable ? <span className="ml-rc-noemail">{c.unavailable}</span> : null}
        {mates ? (
          <span className="ml-nt-mates" title="Isti e-naslov">
            skupni predal: {mates}
          </span>
        ) : null}
      </td>
      <td className="ml-nt-lang">{c.v.lang.toUpperCase()}</td>
      <td>
        <span
          className="ml-nt-pill"
          style={{ background: c.state.bg, borderColor: c.state.bd, color: c.state.fg }}
        >
          {c.state.label}
        </span>
      </td>
    </tr>
  );
});

function postOptions(
  posts: { id: string; sectorId: string; code: string; micro: string }[],
  sectors: { id: string; name: string }[],
) {
  return sectors
    .map((s) => ({
      group: s.name,
      items: posts
        .filter((p) => p.sectorId === s.id)
        .sort((a, b) => a.code.localeCompare(b.code, 'sl', { numeric: true }))
        .map((p) => ({ value: p.id, label: `${p.code} ${p.micro}`.trim() })),
    }))
    .filter((g) => g.items.length > 0);
}

function NoticeFilterRow({
  filters,
  onFilters,
  includeCancelled,
  onIncludeCancelled,
  candidates,
  days,
  sectors,
  posts,
  tags,
}: {
  filters: NoticeDraftHandle['draft']['filters'];
  onFilters: (f: NoticeDraftHandle['draft']['filters']) => void;
  includeCancelled: boolean;
  onIncludeCancelled: (on: boolean) => void;
  candidates: NoticeCandidate[];
  days: { value: string; label: string }[];
  sectors: { value: string; label: string }[];
  posts: ReturnType<typeof postOptions>;
  tags: Parameters<typeof TagFilterChips>[0]['tags'];
}) {
  const set = (patch: Partial<typeof filters>) => onFilters({ ...filters, ...patch });
  const usage = useMemo(() => countUsage(candidates.map((c) => c.v.tagIds)), [candidates]);
  const combo = { withinPortal: true };
  return (
    <div className="ml-nt-filters">
      <TextInput
        size="xs"
        w={170}
        placeholder="Išči po imenu"
        value={filters.query}
        onChange={(e) => set({ query: e.currentTarget.value })}
      />
      <MultiSelect
        size="xs"
        w={190}
        placeholder={filters.states.length ? undefined : 'Potrditev'}
        data={NOTICE_STATES.map((k) => ({ value: k, label: schedStateLabel(k) }))}
        value={filters.states}
        onChange={(v) => set({ states: v })}
        clearable
        comboboxProps={combo}
      />
      <MultiSelect
        size="xs"
        w={150}
        placeholder={filters.dayIds.length ? undefined : 'Dan'}
        data={days}
        value={filters.dayIds}
        onChange={(v) => set({ dayIds: v })}
        clearable
        comboboxProps={combo}
      />
      <MultiSelect
        size="xs"
        w={170}
        placeholder={filters.sectorIds.length ? undefined : 'Sektor'}
        data={sectors}
        value={filters.sectorIds}
        onChange={(v) => set({ sectorIds: v })}
        clearable
        searchable
        comboboxProps={combo}
      />
      <MultiSelect
        size="xs"
        w={190}
        placeholder={filters.postIds.length ? undefined : 'Mesto'}
        data={posts}
        value={filters.postIds}
        onChange={(v) => set({ postIds: v })}
        clearable
        searchable
        comboboxProps={combo}
      />
      <Select
        size="xs"
        w={160}
        data={[
          { value: 'vsi', label: 'Z izmenami ali brez' },
          { value: 'z', label: 'Z izmenami' },
          { value: 'brez', label: 'Brez izmen' },
        ]}
        value={filters.shifts}
        onChange={(v) => set({ shifts: (v ?? 'vsi') })}
        allowDeselect={false}
        comboboxProps={combo}
      />
      <Select
        size="xs"
        w={120}
        data={[
          { value: 'vsi', label: 'Vsi jeziki' },
          { value: 'sl', label: 'Slovensko' },
          { value: 'en', label: 'Angleško' },
        ]}
        value={filters.lang}
        onChange={(v) => set({ lang: (v ?? 'vsi') })}
        allowDeselect={false}
        comboboxProps={combo}
      />
      <Checkbox
        size="xs"
        label="Vključi tudi tiste, ki ne pridejo"
        checked={includeCancelled}
        onChange={(e) => onIncludeCancelled(e.currentTarget.checked)}
      />
      <div className="ml-nt-tags">
        <TagFilterChips
          tags={tags}
          usage={usage}
          filter={filters.tagIds}
          onToggle={(id) =>
            set({
              tagIds: filters.tagIds.includes(id)
                ? filters.tagIds.filter((t) => t !== id)
                : [...filters.tagIds, id],
            })
          }
          onClear={() => set({ tagIds: [] })}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sending it
// ---------------------------------------------------------------------------

/**
 * The last look before the obvestilo goes: to whom, in which languages, which
 * of them share a mail — and who shares an address with somebody ticked but is
 * not ticked themselves, with a way to add them. Then the send, and its report.
 */
function NoticeConfirm({
  notice,
  onSent,
  picked,
  problems,
  overCap,
  housematesToAdd,
  onClose,
}: {
  notice: NoticeDraftHandle;
  /** Runs once the server has accepted the send — even if this dialog is gone by then. */
  onSent: () => void;
  picked: VolunteerRow[];
  /** What stops the send now — asked again as "Dodaj" changes who is on it. */
  problems: string[];
  overCap: boolean;
  housematesToAdd: UnpickedHousemate[];
  /** With the new obvestilo's id once one was sent. */
  onClose: (batchId: string | null) => void;
}) {
  const { send } = useNoticeMutations({ onSent });
  const [batchId, setBatchId] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const batch = useMailBatch(batchId);

  // What the dialog asked about, held once the send is accepted — the draft
  // behind it is cleared at that moment.
  const [sentCount, setSentCount] = useState(0);
  const running = batchId !== null && batch.data?.status !== 'done';
  const report = batch.data?.status === 'done' ? batch.data : null;

  const n = picked.length;
  const mails = mailCount(picked);
  const bySl = picked.filter((v) => v.lang === 'sl').length;
  const together = householdsIn(picked);
  const cancelled = picked.filter((v) => isCancelled(v));
  const mustType = n > TYPE_TO_CONFIRM_ABOVE;
  const confirmed = !mustType || typed.trim() === String(n);

  const close = () => onClose(batchId);

  return (
    <Modal
      opened
      onClose={close}
      title="Pošlji obvestilo"
      centered
      size="md"
      closeOnClickOutside={!running && !send.isPending}
      closeOnEscape={!send.isPending}
      withCloseButton={!send.isPending}
    >
      {report ? (
        <BatchReport batch={report} onClose={close} />
      ) : batchId !== null ? (
        <BatchProgress batch={batch.data ?? null} total={sentCount} onClose={close} />
      ) : (
        <>
          <p className="ml-bulk-lede">
            {n} {people(n)} · {mails} {plural(mails, 'sporočilo', 'sporočili', 'sporočila', 'sporočil')}
            {bySl > 0 && bySl < n ? ` (${bySl} slovensko · ${n - bySl} angleško)` : ''}. Vsak ga
            dobi v svojem jeziku. Kopija ostane v pogovoru vsakega prejemnika.
          </p>

          <div className="ml-eyebrow" style={{ marginTop: 14 }}>
            PREJEMNIKI
          </div>
          <div className="ml-bulk-names">{picked.map((v) => v.name).join(', ')}</div>

          {together.length > 0 ? (
            <>
              <div className="ml-eyebrow" style={{ marginTop: 12 }}>
                SKUPNI PREDALI
              </div>
              <div className="ml-bulk-names">
                {together.map((group) => joinNames(group, 'sl')).join(' · ')} — eno sporočilo na
                predal.
              </div>
            </>
          ) : null}

          {housematesToAdd.length > 0 ? (
            <>
              <div className="ml-eyebrow" style={{ marginTop: 12 }}>
                NA ISTEM NASLOVU, A NI IZBRAN
              </div>
              <ul className="ml-nt-housemates">
                {housematesToAdd.map((h) => (
                  <li key={h.person.id}>
                    <span>
                      {h.person.name} (z {h.withPerson.name})
                      {h.otherLang ? ` — ${LANG_WORD[h.person.lang]}, ločeno sporočilo` : ''}
                      {isCancelled(h.person) ? ' — ne pride' : ''}
                    </span>
                    <Button
                      size="compact-xs"
                      variant="default"
                      onClick={() => {
                        notice.addHousemate(h.person.id);
                        setTyped('');
                      }}
                    >
                      Dodaj
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {cancelled.length > 0 ? (
            <div className="ml-warn" style={{ marginTop: 12 }}>
              Vključeni so tudi, ki ne pridejo: {cancelled.map((v) => v.name).join(', ')}.
            </div>
          ) : null}

          {problems.length > 0 || overCap ? (
            <ul className="ml-nt-problems" style={{ marginTop: 12 }}>
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
              {overCap ? <li>Naenkrat gre lahko največ {BULK_LIMIT} sporočil.</li> : null}
            </ul>
          ) : null}

          {mustType ? (
            <TextInput
              mt={14}
              size="sm"
              label={`Za potrditev vpiši število prejemnikov (${n})`}
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
              inputMode="numeric"
              autoFocus
            />
          ) : null}

          <div className="ml-dialog-actions">
            <div style={{ flex: 1 }} />
            <Button variant="default" size="sm" onClick={close} disabled={send.isPending}>
              Prekliči
            </Button>
            <Button
              size="sm"
              loading={send.isPending}
              disabled={!confirmed || n === 0 || problems.length > 0 || overCap}
              onClick={() =>
                send.mutate(
                  {
                    sl: notice.draft.sl,
                    en: notice.draft.en,
                    volunteerIds: picked.map((v) => v.id),
                    includeCancelled: notice.draft.includeCancelled,
                  },
                  {
                    // The draft itself is cleared by `onSent`, which runs even if
                    // this dialog is closed before the answer arrives.
                    onSuccess: (created) => {
                      setSentCount(n);
                      setBatchId(created.id);
                    },
                  },
                )
              }
            >
              Pošlji {n}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// One that went
// ---------------------------------------------------------------------------

function NoticeRecord({ id }: { id: string }) {
  const labelOf = useCoordinatorLabel();
  const record = useMailNotice(id);
  const [lang, setLang] = useState<Lang>('sl');

  if (record.isPending) {
    return (
      <section className="ml-nt-record">
        <Loader size="sm" />
      </section>
    );
  }
  const data = record.data;
  if (!data) {
    return (
      <section className="ml-nt-record">
        <div className="ml-rc-empty">Obvestila ni bilo mogoče prebrati.</div>
      </section>
    );
  }
  const words = data.notice[lang];
  const sent = data.items.filter((i) => i.status === 'sent').map((i) => i.volunteerName);

  return (
    <section className="ml-nt-record">
      <div className="ml-editor-head">
        <div>
          <div className="ml-editor-name">{data.subject || '— brez zadeve —'}</div>
          <div className="ml-hint">
            Poslal {labelOf(data.createdById, data.createdByName, 'neznan')}, {shortDate(data.createdAt)} ·{' '}
            {data.status === 'done'
              ? `poslano ${data.counts.sent} od ${data.counts.total}`
              : `v teku — ${data.counts.total - data.counts.waiting} od ${data.counts.total}`}
            {data.notice.includeCancelled ? ' · vključeni tudi, ki ne pridejo' : ''}
          </div>
        </div>
        <SegmentedControl
          size="xs"
          value={lang}
          onChange={(v) => setLang(v)}
          data={[
            { value: 'sl', label: 'Slovensko' },
            { value: 'en', label: 'English' },
          ]}
        />
      </div>

      <div className="ml-preview-subject">
        <span className="ml-preview-label">Zadeva</span>
        <span className="ml-preview-value">{words.subject || '— v tem jeziku ni bilo besedila —'}</span>
      </div>
      {words.body.trim() ? (
        <iframe
          className="ml-dialog-frame"
          title="Besedilo obvestila"
          sandbox=""
          srcDoc={`<!doctype html><meta charset="utf-8"><style>body{margin:0;padding:14px 18px;font:14px/1.55 system-ui,sans-serif;color:#1d2a30}</style>${words.body}`}
        />
      ) : null}
      <div className="ml-hint" style={{ marginTop: 6 }}>
        Oznake so prikazane, kot so bile napisane — vsak prejemnik je dobil svoje podatke.
      </div>

      {data.status !== 'done' ? (
        // Mid-send, a recipient being mailed right now reads as "prekinjeno"
        // and one still waiting has no outcome at all — a report drawn from
        // that would list everyone as failed. The report waits for the end.
        <>
          <div className="ml-bulk-result" style={{ marginTop: 16 }}>
            Pošiljam… {data.counts.total - data.counts.waiting} od {data.counts.total}
          </div>
          <Progress
            value={data.counts.total === 0 ? 0 : ((data.counts.total - data.counts.waiting) / data.counts.total) * 100}
            size="sm"
            mt={10}
          />
          <div className="ml-hint" style={{ marginTop: 8 }}>
            Poročilo o tem, kdo je obvestilo prejel, bo tu, ko bo pošiljanje končano.
          </div>
        </>
      ) : (
        <>
          {sent.length > 0 ? (
            <>
              <div className="ml-eyebrow" style={{ marginTop: 16 }}>
                PREJELI
              </div>
              <div className="ml-bulk-names">{sent.join(', ')}</div>
            </>
          ) : null}
          <BatchReportLists batch={data} />
        </>
      )}
    </section>
  );
}
